import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { productsAPI, customersAPI, salesAPI, sucursalesAPI } from '../services/api';
import Toast from './Toast';
import ProductSearchModal from './ProductSearchModal';
import './SaleForm.css';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { getSucursalId, isBotica, isJuguteria } from '../hooks/useSucursal';
import { addPendingSale, searchCachedProducts, searchCachedCustomers } from '../db/localDB';
import { refreshOfflineCache } from '../db/cacheService';
import { registerBackgroundSync } from '../db/syncEngine';

/* ── Avatar color helper ─────────────────────────────── */
const PILL_COLORS = [
    '#ef4444','#f97316','#eab308','#22c55e',
    '#3b82f6','#8b5cf6','#ec4899','#14b8a6','#64748b'
];
const getPillColor = (str = '') => {
    let h = 0;
    for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
    return PILL_COLORS[Math.abs(h) % PILL_COLORS.length];
};

/* ── CartIcon SVG ────────────────────────────────────── */
const CartIcon = ({ size = 18 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} fill="currentColor" viewBox="0 0 16 16">
        <path d="M0 1.5A.5.5 0 0 1 .5 1H2a.5.5 0 0 1 .485.379L2.89 3H14.5a.5.5 0 0 1 .491.592l-1.5 8A.5.5 0 0 1 13 12H4a.5.5 0 0 1-.491-.408L2.01 3.607 1.61 2H.5a.5.5 0 0 1-.5-.5zM5 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4zm7 0a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/>
    </svg>
);

/* ═══════════════════════════════════════════════════════
   COMPONENTE PRINCIPAL
═══════════════════════════════════════════════════════ */
const SaleForm = ({ onSaleSuccess, onShowHistory, forceTipoNegocio = null }) => {

    /* ── Estado offline ──────────────────────────────── */
    const isOnline = useOnlineStatus();
    const [syncStatus, setSyncStatus] = useState('idle');
    const [isSavedOffline, setIsSavedOffline] = useState(false);

    /* ── Estado POS core ─────────────────────────────── */
    const [searchTerm, setSearchTerm] = useState('');
    const [products, setProducts]     = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [cart, setCart] = useState([]);

    const [customerSearch, setCustomerSearch]       = useState('');
    const [customers, setCustomers]                 = useState([]);
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [selectedCustomer, setSelectedCustomer]   = useState(null);

    const [paymentMethod, setPaymentMethod] = useState('efectivo');
    const [amountReceived, setAmountReceived] = useState('');
    const [discountAmount, setDiscountAmount] = useState('');
    const [notes, setNotes] = useState('');

    const [showSuccessModal, setShowSuccessModal] = useState(false);
    const [saleNumber, setSaleNumber] = useState(null);
    const [isSearching, setIsSearching] = useState(false);
    const [toast, setToast] = useState(null);

    /* ── Estado UI ───────────────────────────────────── */
    const [showCobroModal,   setShowCobroModal]   = useState(false);
    const [showSearchModal,  setShowSearchModal]  = useState(false);
    const [showAllProducts,  setShowAllProducts]  = useState(false);
    const [labFilter,        setLabFilter]        = useState('');
    const [showLabInput,     setShowLabInput]     = useState(false);
    const [labInputValue,    setLabInputValue]    = useState('');

    /* ── Refs ────────────────────────────────────────── */
    const searchInputRef  = useRef(null);
    const labInputRef     = useRef(null);
    const cobroAmountRef  = useRef(null);

    /* ── User info ───────────────────────────────────── */
    const user = useMemo(() => {
        try { return JSON.parse(localStorage.getItem('user') || '{}'); }
        catch { return {}; }
    }, []);
    const userId = user.id || null;
    const today  = new Date().toLocaleDateString('es-PE', { day: '2-digit', month: '2-digit', year: 'numeric' });

    const showToast = useCallback((message, type = 'error') => setToast({ message, type }), []);

    /* ══ DATA FETCHING ═══════════════════════════════ */
    const fetchProducts = useCallback(async () => {
        if (!isOnline) return;
        try {
            const sucursalId = getSucursalId();
            if (sucursalId) {
                const res = await sucursalesAPI.getProductos(sucursalId);
                const list = (res.data?.data?.productos || []).map(p => ({
                    ...p,
                    id:         p.producto_id,
                    sale_price: p.precio_efectivo,
                    stock:      p.stock_sucursal !== undefined ? p.stock_sucursal : p.stock_general,
                }));
                setProducts(list);
            } else {
                const res = await productsAPI.getAll();
                setProducts(res.data.data.products || []);
            }
        } catch (err) {
            console.error('Error cargando productos:', err);
        }
    }, [isOnline]);

    const fetchCustomers = useCallback(async () => {
        if (!isOnline) return;
        try {
            const res = await customersAPI.getAll();
            setCustomers(res.data.data.customers || []);
        } catch (err) {
            console.error('Error cargando clientes:', err);
        }
    }, [isOnline]);

    useEffect(() => { fetchProducts(); fetchCustomers(); }, [isOnline]);

    // Polling silencioso cada 60s
    useEffect(() => {
        if (!isOnline) return;
        const iv = setInterval(fetchProducts, 60000);
        return () => clearInterval(iv);
    }, [isOnline, fetchProducts]);

    // Caché offline + Background Sync
    useEffect(() => {
        if (!isOnline || !userId) return;
        refreshOfflineCache(userId, getSucursalId());
        const cleanup = registerBackgroundSync(userId, (result) => {
            if (result.synced > 0) {
                setSyncStatus('done');
                showToast(`✅ ${result.synced} venta(s) sincronizada(s)`, 'success');
                result.warnings?.forEach(w =>
                    showToast(`⚠️ "${w.name}" con stock negativo (${w.currentStock})`, 'warning')
                );
                setTimeout(() => setSyncStatus('idle'), 3000);
            }
        });
        return () => { if (typeof cleanup === 'function') cleanup(); };
    }, [isOnline, userId, showToast]);

    /* ══ KEYBOARD SHORTCUTS ══════════════════════════ */
    useEffect(() => {
        const handler = (e) => {
            const tag = document.activeElement?.tagName;
            const isTyping = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';

            if (e.key === 'F2') {
                e.preventDefault();
                searchInputRef.current?.focus();
                showToast('💡 Modo lector activo — escanea o escribe el código', 'info');
            }
            if (e.key === 'F3') {
                e.preventDefault();
                setShowSearchModal(true);
            }
            if (e.key === 'F5') {
                e.preventDefault();
                if (cart.length > 0) setShowCobroModal(true);
                else showToast('⚠️ Agrega productos al carrito primero', 'warning');
            }
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'b') {
                e.preventDefault();
                searchInputRef.current?.focus();
            }
            if (e.key === 'Escape') {
                if (showCobroModal)  { setShowCobroModal(false);  return; }
                if (showSearchModal) { setShowSearchModal(false); return; }
                if (showLabInput)    { setShowLabInput(false);    return; }
                if (isTyping) { setSearchTerm(''); setFilteredProducts([]); }
            }
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [cart.length, showCobroModal, showSearchModal, showLabInput, showToast]);

    // Enfocar monto al abrir cobro
    useEffect(() => {
        if (showCobroModal && paymentMethod === 'efectivo') {
            setTimeout(() => cobroAmountRef.current?.focus(), 120);
        }
    }, [showCobroModal, paymentMethod]);

    // Enfocar input de laboratorio
    useEffect(() => {
        if (showLabInput) setTimeout(() => labInputRef.current?.focus(), 100);
    }, [showLabInput]);

    /* ══ BÚSQUEDA ════════════════════════════════════ */
    const handleProductSearch = useCallback(async (term) => {
        if (!term || term.trim().length < 2) {
            setFilteredProducts([]);
            setIsSearching(false);
            return;
        }
        setIsSearching(true);
        try {
            if (isOnline) {
                const t = term.toLowerCase();
                setFilteredProducts(products.filter(p =>
                    p.name?.toLowerCase().includes(t) ||
                    p.code?.toLowerCase().includes(t) ||
                    p.laboratorio?.toLowerCase().includes(t) ||
                    p.principio_activo?.toLowerCase().includes(t)
                ));
            } else {
                const cached = await searchCachedProducts(term, userId);
                setFilteredProducts(cached);
            }
        } catch (err) {
            console.error('Error en búsqueda:', err);
        } finally {
            setIsSearching(false);
        }
    }, [isOnline, products, userId]);

    useEffect(() => {
        const id = setTimeout(() => handleProductSearch(searchTerm), 300);
        return () => clearTimeout(id);
    }, [searchTerm, handleProductSearch]);

    const handleCustomerSearch = useCallback(async (term) => {
        if (!term || term.trim().length < 1) { setFilteredCustomers([]); return; }
        if (isOnline) {
            setFilteredCustomers(customers.filter(c =>
                c.name?.toLowerCase().includes(term.toLowerCase()) ||
                c.document_number?.includes(term) ||
                c.phone?.includes(term)
            ));
        } else {
            setFilteredCustomers(await searchCachedCustomers(term, userId));
        }
    }, [isOnline, customers, userId]);

    useEffect(() => {
        const id = setTimeout(() => handleCustomerSearch(customerSearch), 300);
        return () => clearTimeout(id);
    }, [customerSearch, handleCustomerSearch]);

    /* ══ PRODUCTOS A MOSTRAR ═════════════════════════ */
    const displayProducts = useMemo(() => {
        if (searchTerm.length >= 2) return filteredProducts;
        let list = products.filter(p => p.is_active !== false);
        if (labFilter) list = list.filter(p => p.laboratorio?.toLowerCase().includes(labFilter.toLowerCase()));
        return showAllProducts ? list : list.slice(0, 8);
    }, [products, filteredProducts, searchTerm, showAllProducts, labFilter]);

    /* ══ OPERACIONES DEL CARRITO ═════════════════════ */
    const addToCart = useCallback((product, presentation = null, qty = 1) => {
        if (product.stock <= 0) {
            showToast(`❌ ${product.name} no tiene stock disponible`);
            return;
        }

        let cartItemId, current_price, factor, presentation_name, presentation_id;
        if (presentation && presentation.id && presentation.id !== 'base') {
            cartItemId        = `${product.id}-${presentation.id}`;
            current_price     = parseFloat(presentation.price) || product.sale_price;
            factor            = presentation.factor || 1;
            presentation_name = presentation.name;
            presentation_id   = presentation.id;
        } else {
            cartItemId        = `${product.id}-base`;
            current_price     = product.sale_price;
            factor            = 1;
            presentation_name = product.unit || 'Unidad Base';
            presentation_id   = null;
        }

        const existing = cart.find(i => i.cartItemId === cartItemId);
        if (existing) {
            if (product.unit !== 'kg' && existing.quantity >= product.stock) {
                showToast(`❌ Stock máximo disponible: ${Math.floor(product.stock)}`);
                return;
            }
            setCart(prev => prev.map(i =>
                i.cartItemId === cartItemId
                    ? { ...i, quantity: product.unit === 'kg' ? i.quantity : parseFloat(i.quantity || 0) + qty }
                    : i
            ));
        } else {
            setCart(prev => [...prev, {
                ...product, cartItemId,
                quantity: product.unit === 'kg' ? 0 : qty,
                current_price, factor, presentation_name, presentation_id
            }]);
        }

        setSearchTerm('');
        setFilteredProducts([]);
        setTimeout(() => searchInputRef.current?.focus(), 60);
        showToast(`✅ ${product.name} agregado`, 'success');
    }, [cart, showToast]);

    const handlePresentationChange = (oldCartItemId, newPresentationId) => {
        setCart(prev => {
            const idx = prev.findIndex(i => i.cartItemId === oldCartItemId);
            if (idx === -1) return prev;
            const item = prev[idx];
            let newFactor = 1, newPrice = item.sale_price, newPresName = item.unit || 'Unidad Base', newPresId = null;
            let newCartItemId = `${item.id}-base`;
            if (newPresentationId !== 'base') {
                const pres = item.presentations?.find(p => p.id.toString() === newPresentationId.toString());
                if (pres) { newFactor = pres.factor; newPrice = pres.price; newPresName = pres.name; newPresId = pres.id; newCartItemId = `${item.id}-${pres.id}`; }
            }
            const existIdx = prev.findIndex(p => p.cartItemId === newCartItemId && p.cartItemId !== oldCartItemId);
            if (existIdx !== -1) {
                const c = [...prev];
                c[existIdx].quantity = parseFloat(c[existIdx].quantity) + parseFloat(item.quantity);
                c.splice(idx, 1);
                return c;
            }
            const c = [...prev];
            c[idx] = { ...item, cartItemId: newCartItemId, factor: newFactor, current_price: newPrice, presentation_name: newPresName, presentation_id: newPresId };
            return c;
        });
    };

    const updateQuantity = (cartItemId, newQty) => {
        const product = cart.find(i => i.cartItemId === cartItemId);
        if (!product) return;
        const max = product.unit === 'kg' ? parseFloat(product.stock) : Math.floor(product.stock);
        let q = newQty;
        if (newQty !== '' && parseFloat(newQty) > max) q = max;
        if (newQty !== '' && parseFloat(newQty) <= 0) { removeFromCart(cartItemId); return; }
        setCart(prev => prev.map(i => i.cartItemId === cartItemId ? { ...i, quantity: q } : i));
    };

    const updatePrice = (cartItemId, newPrice) =>
        setCart(prev => prev.map(i => i.cartItemId === cartItemId ? { ...i, current_price: newPrice } : i));

    const removeFromCart = (cartItemId) =>
        setCart(prev => prev.filter(i => i.cartItemId !== cartItemId));

    const updateKgByMonto = (cartItemId, montoStr) => {
        const item = cart.find(i => i.cartItemId === cartItemId);
        if (!item) return;
        const monto = parseFloat(montoStr);
        const precio = parseFloat(item.current_price) || 0;
        if (!montoStr || isNaN(monto) || monto <= 0 || precio <= 0) {
            setCart(prev => prev.map(i => i.cartItemId === cartItemId ? { ...i, quantity: 0, _monto: montoStr } : i));
            return;
        }
        const kgs = Math.round((monto / precio) * 1000) / 1000;
        const maxKg = parseFloat(item.stock);
        const finalKg = kgs > maxKg ? maxKg : kgs;
        const finalMonto = parseFloat((finalKg * precio).toFixed(2));
        setCart(prev => prev.map(i =>
            i.cartItemId === cartItemId
                ? { ...i, quantity: finalKg, _monto: String(finalKg === maxKg ? finalMonto : monto) }
                : i
        ));
    };

    const updateKgByWeight = (cartItemId, kgStr) => {
        const item = cart.find(i => i.cartItemId === cartItemId);
        if (!item) return;
        const kg = parseFloat(kgStr);
        const precio = parseFloat(item.current_price) || 0;
        const maxKg = parseFloat(item.stock);
        if (!kgStr || isNaN(kg) || kg < 0) {
            setCart(prev => prev.map(i => i.cartItemId === cartItemId ? { ...i, quantity: 0, _monto: '0' } : i));
            return;
        }
        const finalKg = kg > maxKg ? maxKg : Math.round(kg * 1000) / 1000;
        setCart(prev => prev.map(i =>
            i.cartItemId === cartItemId
                ? { ...i, quantity: finalKg, _monto: String(parseFloat((finalKg * precio).toFixed(2))) }
                : i
        ));
    };

    /* ══ TOTALES ═════════════════════════════════════ */
    const rawSubtotal = useMemo(() =>
        cart.reduce((sum, item) => sum + (parseFloat(item.current_price) || 0) * (parseFloat(item.quantity) || 0), 0)
    , [cart]);

    const discount = parseFloat(discountAmount) || 0;

    const total = useMemo(() => {
        const after = Math.max(0, rawSubtotal - discount);
        return Math.round((after + 0.001) / 0.10) * 0.10;
    }, [rawSubtotal, discount]);

    /* ══ RESET ═══════════════════════════════════════ */
    const handleNuevaVenta = useCallback(() => {
        setCart([]);
        setSelectedCustomer(null);
        setCustomerSearch('');
        setPaymentMethod('efectivo');
        setAmountReceived('');
        setDiscountAmount('');
        setNotes('');
        setSearchTerm('');
        setFilteredProducts([]);
        setShowCobroModal(false);
        setTimeout(() => searchInputRef.current?.focus(), 100);
    }, []);

    /* ══ SUBMIT ══════════════════════════════════════ */
    const handleSubmit = async () => {
        if (cart.length === 0) { showToast('⚠️ Agrega productos al carrito', 'warning'); return; }

        const invalidItems = cart.filter(item => !item.quantity || parseFloat(item.quantity) <= 0);
        if (invalidItems.length > 0) { showToast('⚠️ Tienes productos con cantidad 0 o inválida', 'warning'); return; }

        if (paymentMethod === 'fiado' && !selectedCustomer) {
            showToast('❌ Para vender al fiado debe seleccionar un cliente registrado.', 'error');
            return;
        }

        const customerName  = selectedCustomer?.name || customerSearch.trim() || 'Cliente General';
        const notesValue    = notes.trim() || (!selectedCustomer ? `Cliente: ${customerName}` : '');
        const discountPct   = rawSubtotal > 0 && discount > 0
            ? Math.min((discount / rawSubtotal) * 100, 100) : 0;

        const saleData = {
            customer_id:          selectedCustomer ? selectedCustomer.id : null,
            items: cart.map(item => ({
                product_id:        item.id,
                quantity:          parseFloat(item.quantity) || 0,
                unit_price:        parseFloat(item.current_price) || 0,
                subtotal:          (parseFloat(item.quantity) || 0) * (parseFloat(item.current_price) || 0),
                factor:            item.factor || 1,
                presentation_name: item.presentation_name || 'Unidad Base',
                presentation_id:   item.presentation_id || null
            })),
            payment_method:       paymentMethod,
            discount_percentage:  discountPct,
            notes:                notesValue,
            operation_type:       'venta_directa',
            sucursal_id:          getSucursalId()
        };

        /* ── OFFLINE ── */
        if (!isOnline) {
            try {
                await addPendingSale(saleData, userId);
                setShowCobroModal(false);
                setIsSavedOffline(true);
                setTimeout(() => { handleNuevaVenta(); setIsSavedOffline(false); }, 2000);
            } catch (err) {
                showToast('❌ No se pudo guardar la venta localmente.', 'error');
            }
            return;
        }

        /* ── ONLINE ── */
        try {
            const response = await salesAPI.create(saleData);
            if (!response.data.success) throw new Error(response.data.message || 'Error al crear la venta');

            setSaleNumber(response.data.data.sale.correlativo_local || response.data.data.sale.id);
            setShowCobroModal(false);
            setShowSuccessModal(true);
            if (onSaleSuccess) onSaleSuccess(response.data.data.sale);

            setTimeout(() => {
                setShowSuccessModal(false);
                handleNuevaVenta();
                fetchProducts();
            }, 1600);
        } catch (error) {
            const msg = error.response?.data?.message || error.message || 'Error al procesar la venta';
            showToast(`❌ ${msg}`);
        }
    };

    /* ── Selección desde ProductSearchModal ── */
    const handleSearchModalSelect = (product, presentation, cantidad) => {
        addToCart(product, presentation, parseInt(cantidad) || 1);
        setShowSearchModal(false);
    };

    /* ══ RENDER ══════════════════════════════════════ */
    const sucursalId = getSucursalId();
    const canSubmit  = cart.length > 0;
    // Si se fuerza un tipo desde el wrapper (SaleFormBotica / SaleFormTienda),
    // usarlo; si no, detectarlo automáticamente desde el usuario logueado.
    const botica = forceTipoNegocio !== null
        ? forceTipoNegocio === 'botica'
        : isBotica();
    const juguteria = forceTipoNegocio !== null
        ? forceTipoNegocio === 'juguteria'
        : isJuguteria();

    return (
        <div className="pos-page">

            {/* ════════════════════════════════════════
                HEADER
            ════════════════════════════════════════ */}
            <div className="pos-header">
                <div className="pos-brand">
                    <div className="pos-brand-icon"><CartIcon size={22} /></div>
                    <div>
                        <div className="pos-brand-title">Punto de Venta</div>
                        <div className="pos-brand-sub">{botica ? 'Sistema de Farmacia' : juguteria ? 'Jugutería & Armables' : 'Sistema de Ventas'}</div>
                    </div>
                </div>

                <div className="pos-meta">
                    {/* Status pill */}
                    <div className={`pos-status-pill ${
                        !isOnline           ? 'status--offline' :
                        syncStatus === 'syncing' ? 'status--syncing' :
                        syncStatus === 'done'    ? 'status--done'    : 'status--online'
                    }`}>
                        <span className="pos-status-dot" />
                        {!isOnline                         && 'Sin conexión'}
                        {isOnline && syncStatus === 'syncing' && 'Sincronizando...'}
                        {isOnline && syncStatus === 'done'    && 'Sincronizado'}
                        {isOnline && syncStatus === 'idle'    && 'En línea'}
                    </div>

                    <div className="pos-meta-item">
                        <span className="pos-meta-icon">📅</span>
                        <div><span className="pos-meta-label">Fecha</span><span className="pos-meta-val">{today}</span></div>
                    </div>
                    <div className="pos-meta-item">
                        <span className="pos-meta-icon">👤</span>
                        <div>
                            <span className="pos-meta-label">Usuario</span>
                            <span className="pos-meta-val">{user.name || user.email?.split('@')[0] || 'Usuario'}</span>
                        </div>
                    </div>
                    <div className="pos-meta-item">
                        <span className="pos-meta-icon">🏪</span>
                        <div><span className="pos-meta-label">Caja</span><span className="pos-meta-val">{sucursalId || '1'}</span></div>
                    </div>
                </div>

                <div className="pos-header-btns">
                    <button className="pos-btn-nueva" onClick={handleNuevaVenta}>+ Nueva Venta</button>
                    {onShowHistory && (
                        <button className="pos-btn-historial" onClick={onShowHistory}>↺ Historial</button>
                    )}
                </div>
            </div>

            {/* ════════════════════════════════════════
                BODY — 2 COLUMNAS
            ════════════════════════════════════════ */}
            <div className="pos-body">

                {/* ──────────────────────────────────
                    PANEL IZQUIERDO — BUSCAR PRODUCTO
                ────────────────────────────────── */}
                <div className="pos-left">
                    <div className="pos-section-label">🔍 BUSCAR PRODUCTO</div>

                    {/* Barra de búsqueda */}
                    <div className="pos-searchbar-wrap">
                        <svg className="pos-searchbar-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                            <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85zm-5.242 1.156a5.5 5.5 0 1 1 0-11 5.5 5.5 0 0 1 0 11z"/>
                        </svg>
                        <input
                            ref={searchInputRef}
                            type="text"
                            className="pos-searchbar-input"
                            placeholder="Nombre del producto, código de barras o laboratorio..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter' && filteredProducts.length > 0) addToCart(filteredProducts[0]);
                                if (e.key === 'Escape') { setSearchTerm(''); setFilteredProducts([]); }
                            }}
                            autoFocus
                        />
                        {searchTerm && (
                            <button className="pos-searchbar-clear"
                                onClick={() => { setSearchTerm(''); setFilteredProducts([]); searchInputRef.current?.focus(); }}>✕</button>
                        )}
                        <svg className="pos-barcode-icon" xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="#94a3b8" viewBox="0 0 16 16">
                            <path d="M1.5 1a.5.5 0 0 0-.5.5v3a.5.5 0 0 1-1 0v-3A1.5 1.5 0 0 1 1.5 0h3a.5.5 0 0 1 0 1h-3zM11 .5a.5.5 0 0 1 .5-.5h3A1.5 1.5 0 0 1 16 1.5v3a.5.5 0 0 1-1 0v-3a.5.5 0 0 0-.5-.5h-3a.5.5 0 0 1-.5-.5zM.5 15a.5.5 0 0 0 .5.5h3a.5.5 0 0 0 0-1h-3a.5.5 0 0 0-.5.5zm15 0a.5.5 0 0 1-.5.5h-3a.5.5 0 0 1 0-1h3a.5.5 0 0 1 .5.5z"/>
                            <path d="M3 4.5a.5.5 0 0 1 1 0v7a.5.5 0 0 1-1 0v-7zm2 0a.5.5 0 0 1 1 0v7a.5.5 0 0 1-1 0v-7zm2 0a.5.5 0 0 1 1 0v7a.5.5 0 0 1-1 0v-7zm2 0a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v7a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-7zm3 0a.5.5 0 0 1 1 0v7a.5.5 0 0 1-1 0v-7z"/>
                        </svg>
                    </div>

                    {/* Botones de acceso rápido */}
                    <div className="pos-quick-btns">
                        <button className="pos-quick-btn pos-quick-btn--primary" onClick={() => setShowSearchModal(true)}>
                            🔍 Búsqueda Rápida <kbd>F3</kbd>
                        </button>
                        <button className="pos-quick-btn" onClick={() => searchInputRef.current?.focus()}>
                            ▌▌▌ Lector de Código <kbd>F2</kbd>
                        </button>
                        <button className="pos-quick-btn" onClick={() => {
                            const sinStock = products.filter(p => p.stock <= 0);
                            setFilteredProducts(sinStock);
                            setSearchTerm('__sin_stock__');
                        }}>
                            📦 Productos Sin Stock
                        </button>
                    </div>

                    {/* Chip de filtro por laboratorio */}
                    {labFilter && (
                        <div className="pos-lab-filter-chip">
                            🔬 Laboratorio: <strong>{labFilter}</strong>
                            <button onClick={() => setLabFilter('')}>✕</button>
                        </div>
                    )}

                    {/* ── TABLA DE PRODUCTOS ── */}
                    <div className="pos-products-wrap">
                        <div className="pos-products-title">
                            {searchTerm.length >= 2
                                ? isSearching
                                    ? '🔍 Buscando...'
                                    : `RESULTADOS DE BÚSQUEDA (${displayProducts.length})`
                                : 'PRODUCTOS MÁS USADOS'}
                        </div>

                        <div className="pos-products-table-wrap">
                            <table className="pos-products-table">
                                <thead>
                                    <tr>
                                        <th>{juguteria ? 'ARMABLE / SET' : 'PRODUCTO'}</th>
                                        <th>{juguteria ? 'DIFICULTAD' : botica ? 'LABORATORIO' : 'CATEGORÍA'}</th>
                                        <th>{juguteria ? 'PIEZAS' : 'PRESENTACIÓN'}</th>
                                        <th>STOCK</th>
                                        <th>P. UNIT.</th>
                                        <th style={{ textAlign: 'center' }}>ACCIÓN</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayProducts.length === 0 && (
                                        <tr>
                                            <td colSpan={6} className="pos-table-empty">
                                                {isSearching ? 'Buscando...' : searchTerm.length >= 2 ? 'No se encontraron productos' : 'Cargando productos...'}
                                            </td>
                                        </tr>
                                    )}
                                    {displayProducts.map(product => {
                                        const stockOut = product.stock <= 0;
                                        const stockLow = !stockOut && product.stock <= (product.min_stock || 0);
                                        return (
                                            <tr
                                                key={product.id}
                                                className={`pos-product-row${stockOut ? ' row--agotado' : ''}`}
                                                onDoubleClick={() => addToCart(product)}
                                            >
                                                {/* Producto */}
                                                <td>
                                                    <div className="pos-prod-cell">
                                                        <div className="pos-prod-avatar" style={{ background: getPillColor(product.name) }}>
                                                            {juguteria ? '🧩' : (product.name || '?')[0].toUpperCase()}
                                                        </div>
                                                        <div className="pos-prod-info">
                                                            <span className="pos-prod-name">{product.name}</span>
                                                            <span className="pos-prod-code">{product.code}</span>
                                                            {product.category && (
                                                                <span className="pos-cat-badge">{product.category}</span>
                                                            )}
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* Dificultad (jugutería) / Laboratorio (botica) / Categoría (tienda) */}
                                                <td>
                                                    {juguteria ? (
                                                        product.nivel_dificultad ? (
                                                            <span style={{
                                                                display: 'inline-flex', alignItems: 'center', gap: 3,
                                                                background: product.nivel_dificultad === 'principiante' ? '#ecfdf5' : product.nivel_dificultad === 'medio' ? '#fefce8' : '#fef2f2',
                                                                color: product.nivel_dificultad === 'principiante' ? '#047857' : product.nivel_dificultad === 'medio' ? '#b45309' : '#b91c1c',
                                                                border: `1px solid ${product.nivel_dificultad === 'principiante' ? '#a7f3d0' : product.nivel_dificultad === 'medio' ? '#fde68a' : '#fecaca'}`,
                                                                padding: '2px 7px', borderRadius: 6, fontSize: '0.73rem', fontWeight: 700, textTransform: 'capitalize'
                                                            }}>
                                                                {product.nivel_dificultad === 'principiante' ? '🟢 Principiante' : product.nivel_dificultad === 'medio' ? '🟡 Medio' : '🔴 Avanzado'}
                                                            </span>
                                                        ) : <span style={{ color: '#94a3b8' }}>—</span>
                                                    ) : botica ? (
                                                        <span className="pos-lab">{product.laboratorio || '—'}</span>
                                                    ) : (
                                                        <span className="pos-lab">{product.category || 'General'}</span>
                                                    )}
                                                </td>

                                                {/* Piezas (jugutería) / Presentación */}
                                                <td>
                                                    {juguteria ? (
                                                        product.numero_piezas ? (
                                                            <span style={{
                                                                display: 'inline-flex', alignItems: 'center', gap: 3,
                                                                background: '#f1f5f9', color: '#1e293b',
                                                                padding: '2px 7px', borderRadius: 6, fontSize: '0.75rem', fontWeight: 600
                                                            }}>
                                                                🧩 {product.numero_piezas} pzs
                                                            </span>
                                                        ) : <span style={{ color: '#94a3b8' }}>—</span>
                                                    ) : (
                                                        <span className="pos-pres">
                                                            {product.presentacion || product.unit || '—'}
                                                        </span>
                                                    )}
                                                </td>

                                                {/* Stock */}
                                                <td>
                                                    <span className={`pos-stock-num ${stockOut ? 'stock--out' : stockLow ? 'stock--low' : 'stock--ok'}`}>
                                                        {product.unit === 'kg'
                                                            ? parseFloat(product.stock).toFixed(2)
                                                            : Math.floor(product.stock)}
                                                    </span>
                                                    <span className="pos-stock-unit"> und</span>
                                                    {stockOut && <span className="pos-agotado-badge">AGOTADO</span>}
                                                </td>

                                                {/* Precio */}
                                                <td>
                                                    <span className="pos-unit-price">
                                                        S/ {parseFloat(product.sale_price || 0).toFixed(2)}
                                                    </span>
                                                </td>

                                                {/* Acción */}
                                                <td style={{ textAlign: 'center' }}>
                                                    <button
                                                        className="pos-add-btn"
                                                        onClick={() => addToCart(product)}
                                                        disabled={stockOut}
                                                        title={stockOut ? 'Sin stock' : `Agregar ${product.name}`}
                                                    >
                                                        +
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        {searchTerm.length < 2 && products.filter(p => p.is_active !== false).length > 8 && (
                            <button className="pos-ver-mas" onClick={() => setShowAllProducts(v => !v)}>
                                {showAllProducts
                                    ? 'Ver menos ∧'
                                    : `Ver más productos (${Math.max(0, products.filter(p => p.is_active !== false).length - 8)} más) ∨`}
                            </button>
                        )}
                    </div>
                </div>{/* /pos-left */}

                {/* ──────────────────────────────────
                    PANEL DERECHO — CARRITO
                ────────────────────────────────── */}
                <div className="pos-right">

                    {/* Encabezado carrito */}
                    <div className="pos-cart-header">
                        <span className="pos-cart-title">
                            CARRITO DE COMPRA ({cart.reduce((s, i) => s + (parseFloat(i.quantity) || 0), 0)} items)
                        </span>
                        {cart.length > 0 && (
                            <button className="pos-vaciar-btn" onClick={() => setCart([])}>🗑 Vaciar</button>
                        )}
                    </div>

                    {/* Items */}
                    <div className="pos-cart-content">
                        {cart.length === 0 ? (
                            <div className="pos-cart-empty">
                                <CartIcon size={48} />
                                <p>El carrito está vacío</p>
                                <span>Agrega productos desde la lista o usa <kbd>F3</kbd></span>
                            </div>
                        ) : (
                            <div className="pos-cart-items">
                                <table className="pos-cart-table">
                                    <thead>
                                        <tr>
                                            <th>PRODUCTO</th>
                                            <th>P. UNIT.</th>
                                            <th>CANT.</th>
                                            <th>SUBTOTAL</th>
                                            <th></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {cart.map(item => {
                                            const sub = (parseFloat(item.current_price) || 0) * (parseFloat(item.quantity) || 0);
                                            const priceModified = parseFloat(item.current_price) !== parseFloat(item.sale_price);
                                            const priceBelowCost = parseFloat(item.current_price) < parseFloat(item.purchase_price || 0);
                                            return (
                                                <tr key={item.cartItemId} className="pos-cart-row">
                                                    {/* Producto */}
                                                    <td>
                                                        <div className="pos-cart-prod">
                                                            <div className="pos-cart-avatar" style={{ background: getPillColor(item.name) }}>
                                                                {juguteria ? '🧩' : (item.name || '?')[0].toUpperCase()}
                                                            </div>
                                                            <div className="pos-cart-info">
                                                                <span className="pos-cart-name">{item.name}</span>
                                                                <span className="pos-cart-code">{item.code}</span>
                                                                {juguteria && item.numero_piezas && (
                                                                    <span className="pos-cart-lab">🧩 {item.numero_piezas} pzs</span>
                                                                )}
                                                                {juguteria && item.nivel_dificultad && (
                                                                    <span className="pos-cart-lab">🎮 {item.nivel_dificultad}</span>
                                                                )}
                                                                {!juguteria && item.laboratorio && (
                                                                    <span className="pos-cart-lab">{item.laboratorio}</span>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>

                                                    {/* Precio editable */}
                                                    <td>
                                                        <input
                                                            type="number"
                                                            className={`pos-cart-price-input${priceBelowCost ? ' below-cost' : priceModified ? ' modified' : ''}`}
                                                            value={item.current_price}
                                                            onChange={e => updatePrice(item.cartItemId, e.target.value)}
                                                            onFocus={e => e.target.select()}
                                                            step="0.01"
                                                            title={priceModified ? `Original: S/. ${parseFloat(item.sale_price).toFixed(2)}` : 'Editar precio'}
                                                        />
                                                    </td>

                                                    {/* Cantidad */}
                                                    <td>
                                                        {item.unit === 'kg' ? (
                                                            <div className="pos-kg-wrap">
                                                                <div className="pos-kg-row">
                                                                    <span className="pos-kg-lbl">Kg</span>
                                                                    <input type="number" step="0.001" min="0" max={parseFloat(item.stock)}
                                                                        className="pos-qty-input"
                                                                        value={item.quantity}
                                                                        onChange={e => updateKgByWeight(item.cartItemId, e.target.value)}
                                                                        onFocus={e => e.target.select()} />
                                                                </div>
                                                                <div className="pos-kg-row">
                                                                    <span className="pos-kg-lbl">S/</span>
                                                                    <input type="number" step="0.10" min="0"
                                                                        className="pos-qty-input"
                                                                        value={item._monto !== undefined ? item._monto : parseFloat((parseFloat(item.quantity) * parseFloat(item.current_price || 0)).toFixed(2))}
                                                                        onChange={e => updateKgByMonto(item.cartItemId, e.target.value)}
                                                                        onFocus={e => e.target.select()} />
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="pos-qty-ctrl">
                                                                <button className="pos-qty-btn"
                                                                    onClick={() => updateQuantity(item.cartItemId, parseFloat(item.quantity || 0) - 1)}>−</button>
                                                                <input type="number" className="pos-qty-input center"
                                                                    value={item.quantity}
                                                                    onChange={e => updateQuantity(item.cartItemId, e.target.value)}
                                                                    onFocus={e => e.target.select()}
                                                                    min="1" max={Math.floor(item.stock)} />
                                                                <button className="pos-qty-btn"
                                                                    onClick={() => updateQuantity(item.cartItemId, parseFloat(item.quantity || 0) + 1)}>+</button>
                                                            </div>
                                                        )}
                                                        {/* Presentación (boticas con múltiples) */}
                                                        {botica && item.presentations?.length > 0 && (
                                                            <select className="pos-pres-select"
                                                                value={item.presentation_id || 'base'}
                                                                onChange={e => handlePresentationChange(item.cartItemId, e.target.value)}>
                                                                <option value="base">{item.unit || 'Unidad'} (x1)</option>
                                                                {item.presentations.map(p => (
                                                                    <option key={p.id} value={p.id}>{p.name} (x{p.factor}) — S/{p.price}</option>
                                                                ))}
                                                            </select>
                                                        )}
                                                    </td>

                                                    {/* Subtotal */}
                                                    <td>
                                                        <span className="pos-cart-sub">S/ {sub.toFixed(2)}</span>
                                                    </td>

                                                    {/* Eliminar */}
                                                    <td>
                                                        <button className="pos-cart-del" onClick={() => removeFromCart(item.cartItemId)} title="Quitar">🗑</button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {/* Tip contextual */}
                        {cart.length > 0 && (
                            <div className="pos-tip">
                                💡 <strong>Consejo:</strong> Usa el lector de código para agregar productos más rápido.
                            </div>
                        )}
                    </div>

                    {/* Observaciones */}
                    <div className="pos-obs">
                        <label className="pos-obs-label">OBSERVACIONES <span>(opcional)</span></label>
                        <textarea
                            className="pos-obs-input"
                            placeholder="Agregar notas a la venta..."
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            rows={2}
                        />
                    </div>

                    {/* Resumen financiero */}
                    <div className="pos-summary">
                        <div className="pos-sum-row">
                            <span>SUBTOTAL</span>
                            <span>S/ {rawSubtotal.toFixed(2)}</span>
                        </div>
                        <div className="pos-sum-row">
                            <span>DESCUENTO</span>
                            <div className="pos-discount-wrap">
                                <span>S/</span>
                                <input
                                    type="number"
                                    className="pos-discount-input"
                                    min="0" step="0.10"
                                    value={discountAmount}
                                    onChange={e => setDiscountAmount(e.target.value)}
                                    placeholder="0.00"
                                    onFocus={e => e.target.select()}
                                />
                            </div>
                        </div>
                        <div className="pos-sum-row pos-sum-igv">
                            <span>IGV (0%)</span>
                            <span>S/ 0.00</span>
                        </div>
                        <div className="pos-sum-total">
                            <span>TOTAL</span>
                            <span>S/ {total.toFixed(2)}</span>
                        </div>
                    </div>

                    {/* Botón Cobrar */}
                    <button
                        className="pos-cobrar-btn"
                        onClick={() => canSubmit ? setShowCobroModal(true) : showToast('⚠️ Agrega productos al carrito', 'warning')}
                        disabled={!canSubmit}
                    >
                        <CartIcon size={18} />
                        Cobrar (F5)
                    </button>
                </div>{/* /pos-right */}

            </div>{/* /pos-body */}

            {/* ════════════════════════════════════════
                BARRA DE ACCIONES RÁPIDAS
            ════════════════════════════════════════ */}
            <div className="pos-actions-bar">
                <div className="pos-actions-label">ACCIONES RÁPIDAS</div>
                <div className="pos-actions-row">
                    <button className="pos-action-btn" onClick={() => {
                        const d = prompt('Ingresa el descuento en S/.:');
                        if (d !== null && !isNaN(d) && parseFloat(d) >= 0) setDiscountAmount(d);
                    }}>
                        🏷 Aplicar Descuento
                    </button>
                    <button className="pos-action-btn" onClick={() => setShowLabInput(true)}>
                        🔬 Buscar por Laboratorio
                    </button>
                    <button className="pos-action-btn" onClick={() => setShowSearchModal(true)}>
                        🔍 Consulta de Precios
                    </button>
                    <button className="pos-action-btn pos-action-btn--danger"
                        onClick={() => showToast('🚧 Módulo de devoluciones próximamente', 'info')}>
                        ↩ Devolución (F4)
                    </button>
                    <button className="pos-action-btn"
                        onClick={() => showToast('💾 Funcionalidad de guardar borrador próximamente', 'info')}>
                        💾 Guardar Venta
                    </button>
                </div>
            </div>

            {/* ════════════════════════════════════════
                BARRA DE ATAJOS
            ════════════════════════════════════════ */}
            <div className="pos-shortcut-bar">
                <span className="pos-kbd-chip"><kbd>F2</kbd> Lector de Código</span>
                <span className="pos-kbd-chip"><kbd>F3</kbd> Búsqueda Rápida</span>
                <span className="pos-kbd-chip"><kbd>F4</kbd> Devolución</span>
                <span className="pos-kbd-chip"><kbd>F5</kbd> Cobrar</span>
                <span className="pos-kbd-chip"><kbd>Ctrl+B</kbd> Buscar</span>
                <span className="pos-kbd-chip"><kbd>Esc</kbd> Limpiar Búsqueda</span>
                <span className="pos-shortcut-brand">Sistema de Farmacia v2.0</span>
            </div>

            {/* ════════════════════════════════════════
                MODAL — COBRAR
            ════════════════════════════════════════ */}
            {showCobroModal && (
                <div className="cobro-overlay" onClick={() => setShowCobroModal(false)}>
                    <div className="cobro-modal" onClick={e => e.stopPropagation()}>
                        <div className="cobro-modal-hdr">
                            <h3>💳 Procesar Cobro</h3>
                            <button className="cobro-close" onClick={() => setShowCobroModal(false)}>✕</button>
                        </div>

                        <div className="cobro-total-box">
                            <span>Total a Cobrar</span>
                            <strong>S/. {total.toFixed(2)}</strong>
                        </div>

                        {/* Método de pago */}
                        <div className="cobro-section">
                            <label className="cobro-label">Método de Pago</label>
                            <div className="cobro-pm-tabs">
                                {[
                                    { key: 'efectivo',      label: 'Efectivo',  icon: '💵' },
                                    { key: 'tarjeta',       label: 'Tarjeta',   icon: '💳' },
                                    { key: 'transferencia', label: 'Transf.',   icon: '🏦' },
                                    { key: 'yape',          label: 'Yape',      icon: '📱' },
                                    { key: 'fiado',         label: 'Fiado',     icon: '📝' },
                                ].map(pm => (
                                    <button key={pm.key}
                                        className={`cobro-pm-btn${paymentMethod === pm.key ? ' active' : ''}`}
                                        onClick={() => setPaymentMethod(pm.key)}>
                                        <span className="cobro-pm-icon">{pm.icon}</span>
                                        <span>{pm.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Monto recibido — solo efectivo */}
                        {paymentMethod === 'efectivo' && (
                            <div className="cobro-section">
                                <label className="cobro-label">Paga con</label>
                                <div className="cobro-amount-row">
                                    <span className="cobro-currency">S/.</span>
                                    <input
                                        ref={cobroAmountRef}
                                        type="number" step="0.10"
                                        className="cobro-amount-input"
                                        value={amountReceived}
                                        onChange={e => setAmountReceived(e.target.value)}
                                        onFocus={e => e.target.select()}
                                        onKeyDown={e => { if (e.key === 'Enter') handleSubmit(); }}
                                        placeholder="0.00"
                                    />
                                </div>
                                {amountReceived && parseFloat(amountReceived) >= total && (
                                    <div className="cobro-vuelto">
                                        Vuelto: <strong>S/. {(parseFloat(amountReceived) - total).toFixed(2)}</strong>
                                    </div>
                                )}
                                {amountReceived && parseFloat(amountReceived) < total && (
                                    <div className="cobro-falta">
                                        Falta: S/. {(total - parseFloat(amountReceived)).toFixed(2)}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Cliente */}
                        <div className="cobro-section">
                            <label className="cobro-label">
                                Cliente {paymentMethod === 'fiado'
                                    ? <span className="cobro-required">* Requerido para Fiado</span>
                                    : <span className="cobro-optional">(opcional)</span>}
                            </label>
                            <div className="cobro-customer-wrap" style={{ position: 'relative' }}>
                                <input
                                    type="text"
                                    className="cobro-customer-input"
                                    placeholder="Buscar cliente por nombre, doc. o teléfono..."
                                    value={selectedCustomer ? selectedCustomer.name : customerSearch}
                                    onChange={e => { setCustomerSearch(e.target.value); setSelectedCustomer(null); }}
                                />
                                {selectedCustomer && (
                                    <button className="cobro-customer-clear"
                                        onClick={() => { setSelectedCustomer(null); setCustomerSearch(''); }}>✕</button>
                                )}
                                {filteredCustomers.length > 0 && !selectedCustomer && (
                                    <div className="cobro-customer-dropdown">
                                        {filteredCustomers.slice(0, 5).map(c => (
                                            <div key={c.id} className="cobro-customer-option"
                                                onClick={() => { setSelectedCustomer(c); setCustomerSearch(''); setFilteredCustomers([]); }}>
                                                <strong>{c.name}</strong>
                                                <small>{c.phone || c.document_number || ''}</small>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {selectedCustomer && <div className="cobro-customer-ok">✓ {selectedCustomer.name} seleccionado</div>}
                            {paymentMethod === 'fiado' && !selectedCustomer && (
                                <div className="cobro-warn">⚠️ Debe seleccionar un cliente registrado para vender al fiado</div>
                            )}
                        </div>

                        <button
                            className="cobro-confirm-btn"
                            onClick={handleSubmit}
                            disabled={paymentMethod === 'fiado' && !selectedCustomer}
                        >
                            ✓ Confirmar Venta — S/. {total.toFixed(2)}
                        </button>
                    </div>
                </div>
            )}

            {/* ── Modal buscar por laboratorio ── */}
            {showLabInput && (
                <div className="cobro-overlay" onClick={() => setShowLabInput(false)}>
                    <div className="cobro-modal cobro-modal--sm" onClick={e => e.stopPropagation()}>
                        <div className="cobro-modal-hdr">
                            <h3>🔬 Filtrar por Laboratorio</h3>
                            <button className="cobro-close" onClick={() => setShowLabInput(false)}>✕</button>
                        </div>
                        <div style={{ padding: '16px 20px 20px' }}>
                            <input
                                ref={labInputRef}
                                type="text"
                                className="cobro-customer-input"
                                placeholder="Nombre del laboratorio..."
                                value={labInputValue}
                                onChange={e => setLabInputValue(e.target.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') { setLabFilter(labInputValue); setShowLabInput(false); setLabInputValue(''); }
                                    if (e.key === 'Escape') { setShowLabInput(false); setLabInputValue(''); }
                                }}
                            />
                            <button className="cobro-confirm-btn" style={{ marginTop: 12, borderRadius: 8 }}
                                onClick={() => { setLabFilter(labInputValue); setShowLabInput(false); setLabInputValue(''); }}>
                                Filtrar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ProductSearchModal (F3) */}
            <ProductSearchModal
                isOpen={showSearchModal}
                onClose={() => setShowSearchModal(false)}
                onSelectProduct={handleSearchModalSelect}
                sucursalId={sucursalId}
            />

            {/* ── Modal éxito online ── */}
            {showSuccessModal && (
                <div className="pos-modal-overlay">
                    <div className="pos-modal-success">
                        <div className="pos-modal-icon pos-modal-icon--ok">✓</div>
                        <h3>¡Venta Registrada!</h3>
                        <p className="pos-sale-num">N° {saleNumber}</p>
                        <p>Total: S/. {total.toFixed(2)}</p>
                    </div>
                </div>
            )}

            {/* ── Modal éxito offline ── */}
            {isSavedOffline && (
                <div className="pos-modal-overlay">
                    <div className="pos-modal-success pos-modal-success--offline">
                        <div className="pos-modal-icon pos-modal-icon--offline">📋</div>
                        <h3>¡Venta Guardada!</h3>
                        <p className="pos-offline-note">Sin conexión — se sincronizará cuando regrese el internet.</p>
                        <p>Total: S/. {total.toFixed(2)}</p>
                    </div>
                </div>
            )}

            {/* Toast */}
            {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

        </div>
    );
};

export default SaleForm;